// Argon2id key derivation at a fixed floor (Implementation Plan v02 §6.2, D-77;
// I-1a Batch 3).
//
// The app-lock secret (and the optional recovery code, and later the backup
// passphrase) → Argon2id → a 256-bit AES-GCM wrapping key. The floor is set once
// in I-1a so unlock takes about 1 s on the OLDEST supported phone, and is never
// lowered per device. No PBKDF2 fallback.
//
// Downgrade guard: the cost parameters are stored next to each wrap, so a copy
// of storage could be edited to claim a cheaper cost. Derivation refuses any
// parameters below the floor before doing any work, and the parameters are also
// bound into the wrap's AES-GCM additional data (keys.ts).

import type { computeHash } from 'argon2id'

import { type Bytes, wipe } from './bytes'

export interface Argon2idCost {
  /** Memory in KiB. */
  readonly memorySize: number
  /** Number of passes (iterations). */
  readonly passes: number
  /** Lanes. The WebAssembly build runs them one after another. */
  readonly parallelism: number
}

/**
 * THE floor: 64 MiB, t=5, p=4 (RFC 9106 option 2 memory and lanes, more
 * passes). Set 2026-10-14 from the slowest of the three test phones
 * (device-checklist A7): 64 MiB t=3 p=4 median 553 ms on the Galaxy A54 vs
 * 218 ms on the OnePlus 15R and 140 ms on the Galaxy S25 Ultra. Rule (IP2-28):
 * add passes, not memory, toward ~1 s on the slowest phone → passes =
 * round(3000 / 553) = 5 → about 0.92 s on the A54 (time grows linearly with
 * passes). Never lowered afterwards.
 */
export const ARGON2ID_FLOOR: Argon2idCost = { memorySize: 64 * 1024, passes: 5, parallelism: 4 }

export const SALT_BYTES = 16
const TAG_BYTES = 32

export class BelowFloorError extends Error {
  constructor() {
    super('key derivation cost is below the fixed floor (or outside the allowed range)')
    this.name = 'BelowFloorError'
  }
}

function isPositiveInt(n: number): boolean {
  return Number.isInteger(n) && n >= 1
}

/**
 * Upper bounds, so an edited or corrupted wrap in storage cannot hang or crash
 * unlock with an absurd cost before the AES-GCM check could reject it. Well above
 * any floor this app will set on a phone.
 */
export const ARGON2ID_CEILING: Argon2idCost = { memorySize: 256 * 1024, passes: 32, parallelism: 16 }

/** True when `cost` is at least the floor in memory AND passes (the work factors) and within the ceiling. */
export function meetsFloor(cost: Argon2idCost): boolean {
  return (
    isPositiveInt(cost.memorySize) &&
    isPositiveInt(cost.passes) &&
    isPositiveInt(cost.parallelism) &&
    cost.memorySize >= 8 * cost.parallelism &&
    cost.memorySize >= ARGON2ID_FLOOR.memorySize &&
    cost.passes >= ARGON2ID_FLOOR.passes &&
    cost.memorySize <= ARGON2ID_CEILING.memorySize &&
    cost.passes <= ARGON2ID_CEILING.passes &&
    cost.parallelism <= ARGON2ID_CEILING.parallelism
  )
}

/**
 * Derive a non-extractable AES-GCM wrapping key. There is deliberately NO way to
 * pass a lower floor (tests run at the real one), so no caller can weaken it.
 */
export async function deriveWrappingKey(
  hash: computeHash,
  secret: Uint8Array,
  salt: Bytes,
  cost: Argon2idCost,
): Promise<CryptoKey> {
  if (!meetsFloor(cost)) {
    throw new BelowFloorError()
  }
  if (salt.length < SALT_BYTES) {
    throw new Error('salt too short')
  }
  if (secret.length === 0) {
    throw new Error('empty secret')
  }
  const tag = hash({
    password: secret,
    salt,
    memorySize: cost.memorySize,
    passes: cost.passes,
    parallelism: cost.parallelism,
    tagLength: TAG_BYTES,
  })
  const raw = new Uint8Array(tag)
  wipe(tag)
  try {
    return await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
  } finally {
    wipe(raw)
  }
}
